/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import type {IframePerformance} from '@exc/shared';
import type {MetricsApi} from '@adobe/exc-app/metrics';

export type GettifyFn = (obj: Record<string, any>, path?: string) => void;

/**
 * Parses the script call stack urls from an error instance for this sole purpose.
 * @returns Array of script urls in the call stack.
 */
const parseScriptUrlFromStack = (): (string | undefined)[] => {
  try {
    return Array.from(new Set((new Error().stack || '').split('\n')
      .map(str => {
        const trimmed = str.trim();
        return trimmed.startsWith('at <anonymous>') ? '<anonymous>' : trimmed.match(/(\(|https:)([^:]*):\d+:\d+/)?.[2];
      })
      .filter(str => !!str)));
  } catch (e) {
    return [];
  }
};

export const createGettify = (metricsApi: MetricsApi, window: Window): GettifyFn => {
  const buffer = new Set;
  const metrics = metricsApi.create('exc.module-runtime.util');
  let metricsTimeout: number;

  const trackMetrics = (path: string, payload = {}): void => {
    buffer.add(path.replace(/\[\d*\]$/, '[]'));
    window.clearTimeout(metricsTimeout);
    metricsTimeout = window.setTimeout(() => {
      metrics.event('Object.accessed', {...payload, buffer: Array.from(buffer)});
      buffer.clear();
    }, 500);
  };

  const gettify: GettifyFn = (obj, path = ''): void => {
    if (obj == null || typeof obj !== 'object') {
      return;
    }

    Object.entries(obj).forEach(([key, value]) => {
      const newPath = `${path}${path ? '.' : ''}${key}`;

      Array.isArray(value) ?
        value.forEach((o, i) => gettify(o, `${newPath}[${i}]`)) :
        gettify(value, newPath);

      Object.defineProperty(obj, key, {
        get: () => {
          trackMetrics(newPath, {stack: parseScriptUrlFromStack()});
          return value;
        }
      });
    });
  };

  return gettify;
};

/**
 * Filters performance entries before sending to Shell.
 * @returns performance entries data
 */
export const getPerformanceEntries = (timestamp: number, performance: Performance): IframePerformance|undefined => ({
  done: timestamp,
  entries: performance.getEntries()
    .filter((e: PerformanceEntry) => (
      e.entryType === 'mark' || e.entryType === 'navigation' || e.entryType === 'paint' ||
      (e.entryType === 'resource' && /runtime.*\.js$/.test(e.name))
    )),
  timeOrigin: performance.timeOrigin,
  timing: performance.timing
});
